/* covered.ro design layer — global behaviour (header docking, reveal on scroll). */
(() => {
  const root = document.documentElement;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const ready = (fn) => {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn, { once: true });
  };

  /* Header docking ---------------------------------------------------------
     Sleek's "always" sticky header floats over whatever sits under the top
     58-69px of the screen. Near the top of a page that is the page title. Here
     the header scrolls with the page while the reader goes DOWN through the
     page intro (top of page + first heading) and only floats (slides in)
     further down. Whenever the reader scrolls back UP the header is there:
     once floating it stays floating, and a docked header that has scrolled
     away slides back in as soon as the page moves up. It goes back into the
     page flow only at its own place (scrollY <= the top of the header
     section, marquee showing), where the floating and the docked header are
     drawn at the same spot, so the switch cannot be seen. One class on
     <html>: cv-hdr-dock | cv-hdr-float (styles in cv-chrome.css, section 3).
     No JS: Sleek's default behaviour. */
  const headerDock = () => {
    const HYSTERESIS = 24;
    const UP = 8; // px the page has to move up before a docked header that scrolled away comes back
    const SLIDE_MS = 420; // cv-header-in, cv-chrome.css
    let section = null;
    let header = null;
    let main = null;
    let home = 0; // scrollY up to which the header section sits in its own place
    let enterAt = 0; // below the page intro: scrolling down past it, the header floats
    let state = '';
    let timer = 0;
    let ticking = false;
    let lastY = window.scrollY;
    let upFrom = lastY; // scrollY where the current upward run started
    let slideEnd = 0; // performance.now() when the running slide-in is over

    const instant = () => reduceMotion.matches || document.body.classList.contains('disable-animations');

    // Layout changes above or around the intro (fonts, images, ticker) move the thresholds.
    let pending = false;
    const ro =
      'ResizeObserver' in window
        ? new ResizeObserver(() => {
            if (pending) return;
            pending = true;
            window.requestAnimationFrame(() => {
              pending = false;
              refresh();
            });
          })
        : null;

    const find = () => {
      const prev = [section, main];
      section = document.querySelector('.header-section');
      header = section && section.querySelector('header[data-sticky-type]');
      main = document.getElementById('MainContent');
      // the theme editor re-renders the header section in place
      if (ro && section && section !== prev[0]) ro.observe(section);
      if (ro && main && main !== prev[1]) ro.observe(main);
    };

    const enabled = () =>
      !!(header && main) &&
      ['always', 'on-scroll-up'].includes(header.dataset.stickyType) &&
      header.dataset.enableTransparent !== 'true' &&
      !section.classList.contains('header-transparent');

    // Phone browsers resize the viewport while the reader scrolls (toolbars
    // collapse and expand); keep one height per width so the thresholds do not
    // move under the finger. A real resize (rotation, window) still updates it.
    let vw = -1;
    let vhStable = 0;
    const viewportHeight = () => {
      const w = window.innerWidth;
      const vh = window.innerHeight || root.clientHeight;
      if (w !== vw || Math.abs(vh - vhStable) > 160) {
        vw = w;
        vhStable = vh;
      }
      return vhStable;
    };

    const measure = () => {
      const y = window.scrollY;
      const vh = viewportHeight();
      const h = header.offsetHeight;
      const top = Math.round(main.getBoundingClientRect().top + y); // the in-flow header has fully scrolled off here
      // While the header floats its box is stuck at the top of the screen, so
      // its own place is then taken as "right above the main content".
      home = Math.min(Math.round(section.getBoundingClientRect().top + y), top - section.offsetHeight);
      let intro = top + Math.max(2 * h, Math.round(vh * 0.25));
      // Keep the whole page title clear. It may start below the first screen
      // (product page on a phone, with the browser toolbars shown), so allow
      // up to one and a half screens before giving up on it.
      const h1 = main.querySelector('h1');
      if (h1) {
        const r = h1.getBoundingClientRect();
        if (r.height > 0 && r.top + y < vh * 1.5) intro = Math.max(intro, Math.round(r.bottom + y));
      }
      enterAt = intro + h + HYSTERESIS;
    };

    const set = (next, y) => {
      if (next === state) return;
      window.clearTimeout(timer);
      if (next === 'float') {
        // Slide in from where the header is drawn now: off screen, or (docked
        // near the top) the part of it that still shows.
        const h = header.offsetHeight;
        const from = state === 'dock' ? Math.min(0, Math.max(home - y, -h)) : -h;
        root.style.setProperty('--cv-hdr-from', `${from}px`);
        slideEnd = instant() ? 0 : performance.now() + SLIDE_MS;
      }
      root.classList.toggle('cv-hdr-dock', next === 'dock');
      root.classList.toggle('cv-hdr-float', next === 'float');
      state = next;
    };

    const update = () => {
      ticking = false;
      if (!enabled()) {
        set('');
        return;
      }
      const y = window.scrollY;
      if (y > lastY) upFrom = y;
      lastY = y;
      if (y <= home) {
        // Floating or docked, the header is drawn in its own place here. Dock
        // it (so it scrolls away with the page again), once a slide-in is over.
        const wait = state === 'float' ? slideEnd - performance.now() : 0;
        if (wait <= 0) set('dock', y);
        else {
          window.clearTimeout(timer);
          timer = window.setTimeout(schedule, wait + 20);
        }
      } else if (state === 'float') {
        // stays, all the way back up to its own place
      } else if (y > enterAt) {
        set('float', y);
      } else if (upFrom - y >= UP && y < root.scrollHeight - window.innerHeight) {
        // the reader scrolls up (the rubber band at the page bottom does not count)
        set('float', y);
      } else {
        set('dock', y);
      }
    };

    const schedule = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    };

    function refresh() {
      find();
      if (enabled()) measure();
      schedule();
    }

    refresh();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', refresh, { passive: true });
    window.addEventListener('load', refresh, { once: true });
    window.addEventListener('pageshow', refresh);
    document.addEventListener('shopify:section:load', refresh);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(refresh);
  };

  ready(headerDock);

  ready(() => {
    const animationsOff = document.body.classList.contains('disable-animations');
    if (reduceMotion.matches || animationsOff || !('IntersectionObserver' in window)) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-revealed');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -6% 0px', threshold: 0.08 }
    );

    const observe = (scope) => {
      scope.querySelectorAll('[data-cv-reveal]:not(.is-revealed)').forEach((el) => observer.observe(el));
    };

    // What is already on screen when this runs was painted before it (the script is deferred): it is marked
    // revealed before .cv-motion goes on, so it is never hidden and faded back in. Only content outside the
    // viewport (below the fold, or off to the side in a rail) waits for the observer.
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    document.querySelectorAll('[data-cv-reveal]').forEach((el) => {
      const rect = el.getBoundingClientRect();
      if (rect.width && rect.top < vh && rect.bottom > 0 && rect.left < vw && rect.right > 0) el.classList.add('is-revealed');
    });

    root.classList.add('cv-motion');
    observe(document);

    // Theme editor: sections are re-rendered in place.
    document.addEventListener('shopify:section:load', (event) => observe(event.target));

    // Safety net: never leave content hidden if an observer misses it.
    window.addEventListener(
      'load',
      () => {
        window.setTimeout(() => {
          document.querySelectorAll('[data-cv-reveal]:not(.is-revealed)').forEach((el) => {
            const rect = el.getBoundingClientRect();
            if (rect.top < window.innerHeight && rect.bottom > 0) el.classList.add('is-revealed');
          });
        }, 1200);
      },
      { once: true }
    );
  });

  /* PayPo box (snippets/cv-paypo-box.liquid, product page): "Cum funcționează" opens the dialog of its own box;
     the close button or a click on the backdrop closes it (Esc too, natively). One listener on the document, so a
     box the theme editor re-renders keeps working. */
  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-cv-paypo-open]');
    if (opener) {
      const box = opener.closest('[data-cv-paypo]');
      const dialog = box && box.querySelector('dialog');
      if (dialog && typeof dialog.showModal === 'function' && !dialog.open) dialog.showModal();
      return;
    }
    const dialog = event.target.closest('.cv-paypo-modal');
    if (dialog && (event.target === dialog || event.target.closest('[data-cv-paypo-close]'))) dialog.close();
  });
})();
